import {
    Box,
    Button,
    CircularProgress,
    ClickAwayListener,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Popover,
    Stack,
    styled,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { formatDate, formatPrice, getRelativeTime } from 'src/utils/FormatHelpers';
import { AiMailConversationResource } from 'src/backend/market';
import useDialog from 'src/hooks/useDialog';
import store, { RootState } from 'src/redux/store';
import PageHeader from 'src/components/display/PageHeader';
import { nav } from 'src/statics/navigations';
import GppBadTwoToneIcon from '@mui/icons-material/GppBadTwoTone';
import { wT } from 'src/utils/wizardTranslations';
import KeyboardArrowDownTwoToneIcon from '@mui/icons-material/KeyboardArrowDownTwoTone';
import { InternalCalculationResource } from 'src/backend/internalCalc';
import CalculationStatus from 'src/components/display/CalculationStatus/CalculationStatus';
import EastTwoToneIcon from '@mui/icons-material/EastTwoTone';
import RedeemTwoToneIcon from '@mui/icons-material/RedeemTwoTone';
import { ProCalcSavingStatus } from 'src/redux/slices/proCalc.reducer';
import { ProCalcUpdateTypes, selectProCalcMessages, selectProCalcPriceDetails, selectProCalcPriceLoading, updateProCalc } from 'src/redux/shared/proCalc.common';
import { deleteProCalc } from 'src/redux/thunks/proCalcs.thunk';
import { CalculationResource } from 'src/backend/coreCalc';
import { getComputationValue, getNumberValue } from 'src/utils/CalcHelpers';

const IconButtonEdit = styled(IconButton)(
    ({ theme }) => `
        padding: ${theme.spacing(0.8)};
  
        .MuiSvgIcon-root {
            font-size: ${theme.typography.pxToRem(20)};
        }
    `
);
const StyledListItemText = styled(ListItemText)(
    ({ theme }) => `
        .MuiListItemText-primary {
            display: flex;
        }
    `
);

interface Props {
    calculation?: CalculationResource;
    savingStatus?: ProCalcSavingStatus;
    mailConversation: AiMailConversationResource;
}

const PROCalcHeader: React.FC<Props> = ({ calculation, savingStatus, mailConversation }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const dialog = useDialog();
    const { t }: { t: any } = useTranslation();
    const theme = useTheme();
    const [newName, setNewName] = useState<string>(calculation?.name);
    const [isRenaming, setRename] = useState<boolean>(false);
    const [isShowingErrors, setShowingErrors] = useState<boolean>(false);
    const ref = useRef<any>(null);
    const s = store.getState();
    const isPriceLoading = useSelector(selectProCalcPriceLoading);
    const messages = useSelector(selectProCalcMessages);

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleBack = () => {
        navigate(nav.CALCULATIONS.path);
    };

    const handleFinish = () => {
        navigate(nav.CALCULATIONS.sub.VIEW.path.replace(':id', calculation?.id));
    };
    const handleOffer = () => {
        navigate(nav.CALCULATIONS.sub.VIEW.path.replace(':id', calculation?.id) + '#open');
    };

    const handleDelete = () => {
        dialog.openDialog({
            severity: 'error',
            okText: t('lschen'),
            cancelText: t('nichtLschen'),
            component: <Typography>{t('bistDuSicherDassDuDieseKalkulationLschenMchtest')}</Typography>,
            title: t('kalulationLschen'),
            okCallback: () => {
                dispatch(deleteProCalc(calculation.id));
                handleBack();
                dialog.closeDialog();
            }
        });
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        event.persist();
        setNewName(event.target.value);
    };

    useEffect(() => {
        setNewName(calculation?.name || '');
    }, [calculation?.name]);

    const handleRename = async (): Promise<void> => {
        setRename(false);
        if (newName === calculation?.name) return;
        dispatch(updateProCalc(ProCalcUpdateTypes.NAME, newName));
    };

    const handleKeypress = (event) => {
        if (event.key === 'Enter') {
            event.target.blur();
        }
    };

    const price = useMemo(() => getComputationValue(calculation?.masterComputation, calculation?.masterVariables) || 0, [calculation?.masterComputation, calculation?.masterVariables]);

    const errorInfo = () => {
        return (
            <>
                <Button
                    sx={{ px: 1, pt: 0, pb: 0.15, mt: -0.1, flexShrink: 0, fontSize: 12 }}
                    startIcon={<GppBadTwoToneIcon sx={{ fontSize: '15px !important' }} />}
                    endIcon={<KeyboardArrowDownTwoToneIcon sx={{ fontSize: '15px !important', mr: 0.2 }} />}
                    variant="contained"
                    color="error"
                    onClick={() => setShowingErrors(true)}
                    ref={ref}
                >
                    {t('xErrors', { x: messages.errors.length })}
                </Button>

                <Popover
                    anchorEl={ref.current}
                    onClose={() => setShowingErrors(false)}
                    open={isShowingErrors}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                >
                    <List sx={{ maxWidth: '30rem', maxHeight: '50rem' }}>
                        {messages.errors.map((message, index) => (
                            <ListItem key={index}>
                                <StyledListItemText primary={<>{wT(message.messageId + '.short', s)}</>} secondary={wT(message.messageId + '.long', s)} />
                            </ListItem>
                        ))}
                    </List>
                </Popover>
            </>
        );
    };

    return (
        <PageHeader
            handleBack={handleBack}
            mainTitle={
                <Stack overflow="hidden">
                    <Box display="flex" alignItems="center" position="relative">
                        <span id="titleEditCalcHeader" style={{ maxWidth: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                            {calculation?.customer?.companyName || calculation?.customer?.email || t('newCalculation')}
                        </span>
                        &nbsp;
                        {isRenaming ? (
                            <ClickAwayListener onClickAway={handleRename}>
                                <TextField
                                    value={newName}
                                    size="small"
                                    onBlur={handleRename}
                                    onChange={handleChange}
                                    onKeyDown={handleKeypress}
                                    variant="outlined"
                                    margin="none"
                                    sx={{ m: 0 }}
                                    inputProps={{ sx: { py: 0.6 }, maxLength: 30 }}
                                    fullWidth
                                />
                            </ClickAwayListener>
                        ) : (
                            <Typography variant="h3" component="span" onClick={() => setRename(true)} noWrap maxWidth="34rem">
                                {calculation?.name && ' | ' + calculation?.name}
                            </Typography>
                        )}
                        <Box display="flex" alignItems="center" pl={calculation?.name && 0.5}>
                            <Tooltip arrow placement="top" title={t('rename')}>
                                <IconButtonEdit onClick={() => setRename(true)} color="primary">
                                    <EditTwoToneIcon />
                                </IconButtonEdit>
                            </Tooltip>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Tooltip arrow placement="top" title={t('delete')}>
                                <IconButtonEdit onClick={handleDelete} color="secondary">
                                    <DeleteTwoToneIcon />
                                </IconButtonEdit>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Stack direction="row" alignItems="center" gap={1}>
                        {calculation?.createdAt && (
                            <Tooltip title={formatDate(calculation.createdAt)}>
                                <Typography variant="subtitle2">
                                    {t('created')} {getRelativeTime(calculation.createdAt)}
                                </Typography>
                            </Tooltip>
                        )}
                        {messages.errors.length > 0 && errorInfo()}
                    </Stack>
                </Stack>
            }
            secondaryTitle={
                <>
                    {t(nav.CALCULATIONS.sub.EDIT.caption, { id: calculation?.id })} - {t(savingStatus)}
                </>
            }
            tabTitle={t(nav.CALCULATIONS.sub.EDIT.tabTitle, { id: calculation?.id })}
            sx={{ py: 0, '& > div': { height: '100%' }, position: 'sticky', top: isSmallScreen ? 0 : theme.header.height, zIndex: 106, backgroundColor: 'rgb(249 250 252)' }}
        >
            {price != null && (
                <>
                    <Stack direction="row" justifyContent="space-around" alignItems="center" gap={3} height="100%">
                        <Box textAlign="center">
                            <Typography variant="subtitle2" gutterBottom>
                                {t('status')}
                            </Typography>
                            <CalculationStatus status={calculation.status} mailConversation={mailConversation} />
                        </Box>
                        <Box px={2} py={1.4} textAlign="center">
                            <Typography variant="subtitle2">{t('price')}</Typography>
                            <Typography variant="h3" position="relative" sx={{ color: isPriceLoading ? 'transparent' : theme.colors.primary.main }}>
                                {formatPrice(price, false, calculation.settings?.numberFormat, calculation.settings?.currency)}
                                {isPriceLoading && (
                                    <Stack sx={{ position: 'absolute', justifyContent: 'center', alignItems: 'center', top: 0, right: 0, bottom: 0, left: 0 }}>
                                        <CircularProgress color="primary" size={25} />
                                    </Stack>
                                )}
                            </Typography>
                        </Box>

                        <Stack px={2} py={1.4} gap={0.5}>
                            {/* <Button size="small" variant="contained" startIcon={<RedeemTwoToneIcon />} onClick={handleOffer}>
                                Angebot erstellen
                            </Button> */}
                            <Button size="small" variant="outlined" endIcon={<EastTwoToneIcon />} onClick={handleFinish}>
                                Zur Übersicht
                            </Button>
                        </Stack>
                    </Stack>
                </>
            )}
        </PageHeader>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        calculation: state.proCalc.calculation,
        savingStatus: state.proCalc.savingStatus,
        mailConversation: state.proCalc.mailConversation
    };
};
export default connect(mapStateToProps)(PROCalcHeader);
